import {defineStore} from 'pinia'
import {
  BILLING_SESSION_NAME,
  CART_ITEMS_COOKIE_NAME, DELIVERY_SESSION_NAME,
  EMPTY_BILLING_DATA,
  EMPTY_DELIVERY_DATA,
  EMPTY_SHIPPING_DATA, PAYMENT_METHOD_SESSION_NAME, SHIPPING_SESSION_NAME, USER_SESSION_NAME
} from '~/utils/userUtils'
import {useProductApi} from '~/composables/api/useProductApi'
import {cookieOptions} from '~/utils/cookieUtils'
import Cookies from 'js-cookie'
import {useDeliveryMethodApi} from '~/composables/api/useDeliveryMethodApi'
import {useShopApi} from '~/composables/api/useShopApi'
import {usePaymentMethodApi} from '~/composables/api/usePaymentMethodApi'
import {isEmpty} from 'lodash'

export const useCartStore = defineStore('cart', {
  state: () => ({
    items: [],
    ids: [],
    user: null,
    order: null,
    userId: null,
    deliveryMethods: [],
    deliveryLocations: [],
    paymentMethods: [],
    deliveryData: {...EMPTY_DELIVERY_DATA},
    billingData: {...EMPTY_BILLING_DATA},
    shippingData: {...EMPTY_SHIPPING_DATA},
    paymentMethod: 'card',
  }),

  actions: {
    addItem(cartItem, ignoreCookie = false) {
      const foundItemIndex = this.items.findIndex((item) => item.product.id === cartItem.product.id && item.selected_variant === cartItem.selected_variant)

      // No existe
      if (foundItemIndex === -1) {
        this.items.push(cartItem)
        this.ids.push({product: cartItem.product.id, qty: cartItem.qty, selected_variant: cartItem.selected_variant})
      }

      // Existe
      if (foundItemIndex > -1) {
        this.items[foundItemIndex].qty += cartItem.qty
        this.ids[foundItemIndex].qty += cartItem.qty
      }

      if (!ignoreCookie) {
        this.saveCartState()
      }

      return this.items
    },

    replaceItem(cartItem, ignoreCookie = false) {
      const foundItemIndex = this.items.findIndex((item) => item.product.id === cartItem.product.id && item.selected_variant === cartItem.selected_variant)

      if (foundItemIndex > -1) {
        this.items[foundItemIndex].qty = cartItem.qty
        this.ids[foundItemIndex].qty = cartItem.qty

        if (!ignoreCookie) {
          const cartItemsCookie = useCookie(CART_ITEMS_COOKIE_NAME, cookieOptions)
          cartItemsCookie.value = JSON.stringify(this.ids)
        }
      }

      return this.items
    },

    removeItem(cartItem, ignoreCookie = false) {
      const foundItemIndex = this.items.findIndex((item) => item.product.id === cartItem.product.id && item.selected_variant === cartItem.selected_variant)

      if (foundItemIndex > -1) {
        this.items.splice(foundItemIndex, 1)
        this.ids.splice(foundItemIndex, 1)

        if (!ignoreCookie) {
          const cartItemsCookie = useCookie(CART_ITEMS_COOKIE_NAME, cookieOptions)
          cartItemsCookie.value = JSON.stringify(this.ids)
        }
      }

      return this.items
    },

    setUser(user) {
      this.user = user
      const sessionBillingData = useSessionStorage(USER_SESSION_NAME, {})
      sessionBillingData.value = user
    },

    clearUser() {
      this.user = null
      const sessionBillingData = useSessionStorage(USER_SESSION_NAME, {})
      sessionBillingData.value = null
    },

    clearCart(ignoreCookie = false) {
      this.clearItems()
      this.clearPaymentMethods()
      this.clearDeliveryLocations()
      this.clearDeliveryMethods()
      //stored in session data
      this.clearUser()
      this.clearDelivery()
      this.clearBilling()
      this.clearShipping()
      this.clearPaymentMethod()

      if (!ignoreCookie) {
        this.saveCartState()
      }
    },

    setFlashCreatedOrder(order) {
      this.order = order
    },

    setDelivery(deliveryData) {
      this.deliveryData = deliveryData
      const sessionBillingData = useSessionStorage(DELIVERY_SESSION_NAME, {})
      sessionBillingData.value = deliveryData
    },

    clearDelivery() {
      this.deliveryData = {...EMPTY_DELIVERY_DATA}
      const sessionBillingData = useSessionStorage(DELIVERY_SESSION_NAME, {})
      sessionBillingData.value = null
    },

    setBilling(billingData) {
      this.billingData = billingData
      const sessionBillingData = useSessionStorage(BILLING_SESSION_NAME, {})
      sessionBillingData.value = billingData
    },

    clearBilling() {
      this.billingData = {...EMPTY_BILLING_DATA}
      const sessionBillingData = useSessionStorage(BILLING_SESSION_NAME, {})
      sessionBillingData.value = null
    },

    setPaymentMethod(paymentMethod) {
      this.paymentMethod = paymentMethod
      const sessionBillingData = useSessionStorage(PAYMENT_METHOD_SESSION_NAME, 'card')
      sessionBillingData.value = paymentMethod
    },

    clearPaymentMethod() {
      this.paymentMethod = 'card'
      const sessionBillingData = useSessionStorage(PAYMENT_METHOD_SESSION_NAME, 'card')
      sessionBillingData.value = 'card'
    },

    setPaymentMethods(methods) {
      this.paymentMethods = methods
    },

    clearPaymentMethods() {
      this.paymentMethods = []
    },

    setDeliveryMethod(method) {
      if (!this.deliveryData) {
        this.deliveryData = {}
      }

      this.deliveryData.type = method
    },

    setDeliveryMethods(methods) {
      this.deliveryMethods = methods
    },

    clearDeliveryMethods() {
      this.deliveryMethods = []
    },

    setDeliveryLocations(locations) {
      this.deliveryLocations = locations
    },

    clearDeliveryLocations() {
      this.deliveryLocations = []
    },

    setShipping(shippingData) {
      this.shippingData = (!shippingData || isEmpty(shippingData)) ? {...EMPTY_SHIPPING_DATA} : shippingData
      const sessionBillingData = useSessionStorage(SHIPPING_SESSION_NAME, {})
      sessionBillingData.value = shippingData
    },

    clearShipping() {
      this.shippingData = {...EMPTY_SHIPPING_DATA}
      const sessionBillingData = useSessionStorage(SHIPPING_SESSION_NAME, {})
      sessionBillingData.value = null
    },

    clearItems() {
      this.items = []
      this.ids = []

      this.saveCartState()
    },

    selectDefaultDeliveryMethod() {
      if (!this.deliveryData) {
        this.deliveryData = {}
      }

      const defaultDeliveryMethod = this.deliveryMethods[0]
      this.deliveryData.type = defaultDeliveryMethod.type
    },

    async reloadCartItems() {
      const localItems = this.ids

      await this.loadCartItems(localItems)
    },

    async loadCartItems(localItems) {
      this.clearCart(true)
      const productApi = useProductApi()

      if (localItems?.length > 0) {
        const productIds = localItems.map(item => item.product)

        const products = await productApi.getListByIds(productIds)

        products.map((product) => {
          const foundLocal = localItems.find(item => item.product === product.id)

          if (foundLocal) {
            foundLocal.product = product

            this.addItem(foundLocal, true)
          }
        })

        this.saveCartState()
      }
    },

    saveCartState() {
      Cookies.set(CART_ITEMS_COOKIE_NAME, JSON.stringify(this.ids), cookieOptions)
    },

    async loadDeliveryMethods(force = false) {
      try {
        if (this.deliveryMethods.length > 0 && !force) {
          return
        }
        const deliveryMethodApi = useDeliveryMethodApi()
        const deliveryMethodResult = await deliveryMethodApi.getList()
        this.selectedDeliveryMethodIndex = 0
        this.setDeliveryMethods(deliveryMethodResult)
      } catch (e) {
        console.error('Error seleccionando primer campo')
      }
    },

    async loadPickupLocations(force = false) {
      try {
        if (this.deliveryLocations.length > 0 && !force) {
          return
        }
        const shopApi = useShopApi()
        const pickupShopsResult = await shopApi.getPickupList()
        this.setDeliveryLocations(pickupShopsResult)
      } catch (e) {
        console.error('Error obteniendo localizaciones')
      }
    },

    async loadPaymentMethods(force = false) {
      try {
        if (this.paymentMethods.length > 0 && !force) {
          return
        }
        const paymentMethodApi = usePaymentMethodApi()
        const paymentMethodResult = await paymentMethodApi.getList()
        const paymentMethods = paymentMethodResult.map((paymentMethod) => ({
          id: paymentMethod.id,
          text: paymentMethod.title,
          value: paymentMethod.type,
          description: paymentMethod.description,
        }))

        this.setPaymentMethods(paymentMethods)
      } catch (e) {
        console.error('Error obteniendo métodos de pago')
      }
    },
    async loadCartSessionData(force = false) {
      const sessionDeliveryData = useSessionStorage(DELIVERY_SESSION_NAME, {})
      const sessionBillingData = useSessionStorage(BILLING_SESSION_NAME, {})
      const sessionShippingData = useSessionStorage(SHIPPING_SESSION_NAME, {})
      const sessionUserData = useSessionStorage(USER_SESSION_NAME, {})
      const sessionPaymentMethodData = useSessionStorage(PAYMENT_METHOD_SESSION_NAME, '')

      if (!isEmpty(sessionDeliveryData.value) || force) {
        this.setDelivery(sessionDeliveryData.value)
      }
      if (!isEmpty(sessionBillingData.value) || force) {
        this.setBilling(sessionBillingData.value)
      }
      if (!isEmpty(sessionShippingData.value) || force) {
        this.setShipping(sessionShippingData.value)
      }
      if (!isEmpty(sessionUserData.value) || force) {
        this.setUser(sessionUserData.value)
      }
      if (!isEmpty(sessionPaymentMethodData.value) || force) {
        this.setPaymentMethod(sessionPaymentMethodData.value)
      }
    },
    setSkipInvoice(value) {
      this.billingData.ignoreInvoice = value
    }
  },

  getters: {
    cartUser: (state) => state.user,
    subtotal: (state) => state.items.reduce((acc, current) => {
      let currentPriceET = current.product.price_et
      if (current.selected_variant) {
        const product = current.product
        const selectedVariant = product.variants.find(variant => variant.id === current.selected_variant)
        currentPriceET = selectedVariant ? selectedVariant.price_et : currentPriceET
        if (currentPriceET === 0) {
          currentPriceET = current.product.price_et
        }
      }
      return acc + (current.qty * currentPriceET)
    }, 0),
    taxes: (state) => {
      const productsTaxes = state.items.reduce((acc, current) => {
        let currentTaxes = current.product.price_taxes
        if (current.selected_variant) {
          const product = current.product
          const selectedVariant = product.variants.find(variant => variant.id === current.selected_variant)
          currentTaxes = selectedVariant ? selectedVariant.price_taxes : currentTaxes
          if (currentTaxes === 0) {
            currentTaxes = current.product.price_taxes
          }
        }
        return acc + (current.qty * currentTaxes)
      }, 0)
      const shippingTaxes = state.shippingData?.price?.taxes || 0

      return productsTaxes + shippingTaxes
    },
    total: (state) => {
      const productsTotal = state.items.reduce((acc, current) => {
        let currentPrice = current.product.price
        if (current.selected_variant) {
          const product = current.product
          const selectedVariant = product.variants.find(variant => variant.id === current.selected_variant)
          currentPrice = selectedVariant ? selectedVariant.price : currentPrice
          if (currentPrice === 0) {
            currentPrice = current.product.price
          }
        }
        return acc + (current.qty * currentPrice)
      }, 0)
      const shippingTotal = state.shippingData?.price?.price || 0

      return productsTotal + shippingTotal
    },
    shipping: (state) => state.shippingData?.price?.price || 0,
    hasItems: (state) => state.items.length > 0,
    itemsCount: (state) => state.items.length || 0,
    createdOrder: (state) => state.order,
    cartDelivery: (state) => state.deliveryData,
    cartBilling: (state) => state.billingData,
    cartShipping: (state) => state.shippingData,
    cartPaymentMethod: (state) => state.paymentMethod,
    cartPickupLocations: (state) => state.deliveryLocations,
    skipInvoice: (state) => state.billingData.ignoreInvoice,
    hasSessionData: (state) => {
      const sessionDeliveryData = useSessionStorage(DELIVERY_SESSION_NAME, {})
      const sessionBillingData = useSessionStorage(BILLING_SESSION_NAME, {})
      const sessionShippingData = useSessionStorage(SHIPPING_SESSION_NAME, {})
      const sessionUserData = useSessionStorage(USER_SESSION_NAME, {})
      const sessionPaymentMethodData = useSessionStorage(PAYMENT_METHOD_SESSION_NAME, '')

      return !isEmpty(sessionDeliveryData.value) ||
        !isEmpty(sessionBillingData.value) ||
        !isEmpty(sessionShippingData.value) ||
        !isEmpty(sessionUserData.value) ||
        !isEmpty(sessionPaymentMethodData.value)
    },
    hasCartItems: (state) => state.items && state.items.length > 0,
    hasDeliveryData: (state) => {
      if (state.deliveryData.type === 'pickup') {
        return !!state.deliveryData?.id
      }

      return !!state.deliveryData?.company && !!state.deliveryData?.streetAddress && !!state.deliveryData?.locality && !!state.deliveryData.region && !!state.deliveryData?.postalCode && !!state.deliveryData?.country
    },
    hasBillingData: (state) => {
      if (state.billingData.ignoreInvoice) {
        return true
      }

      return !!state.billingData?.streetAddress && !!state.billingData?.locality && !!state.billingData.region && !!state.billingData?.postalCode
    },
    hasShippingData: (state) => !!state.shippingData?.id,
    hasSelectedDeliveryMethod: (state) => !!state.deliveryData?.type,
    selectedDeliveryMethod: (state) => state.deliveryData?.type || 'courier',
    selectedDeliveryMethodData: (state) => state.deliveryMethods.find((method) => method.type === state.deliveryData?.type),
    selectedPaymentMethod: (state) => state.paymentMethod || 'card',
    selectedPaymentMethodData: (state) => state.paymentMethods.find((method) => method.type === state.paymentMethod),
    selectedDeliveryLocation: (state) => state.deliveryData?.location || null,
    selectedDeliveryLocationData: (state) => state.deliveryLocations.find((location) => location.id === state.deliveryData?.location),
    hasDeliveryMethods: (state) => state.deliveryMethods && state.deliveryMethods.length > 0,
    hasDeliveryLocations: (state) => state.deliveryLocations && state.deliveryLocations.length > 0,
  }
})
